import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useCallback } from "react";

import { AssetLabel } from "@/components/asset-label";
import { type LogView, OpportunityLog } from "@/features/opportunities/OpportunityLog";
import { StatsSummary } from "@/features/opportunities/StatsSummary";
import { DEFAULT_RANGE, pairSearch } from "@/features/opportunities/search";
import { TAB_ACTIVE_CLASS, TAB_CLASS } from "@/features/opportunities/tabs";
import { useGetOpportunityStats } from "@/generated/api/opportunities/opportunities";
import { tokenIdOfPair } from "@/lib/cardanoscan";
import { cn } from "@/lib/utils/cn";

export const Route = createFileRoute("/stats/pairs/$pair")({ component: PairBreakdown, validateSearch: pairSearch });

const VIEWS = [
    { view: "overview", label: "Overview" },
    { view: "log", label: "Log" },
] as const;

/** One pair's breakdown: the overview or the log, scoped to it. [[spec-per-pair-breakdown-of-arbitrage-opportunity-statistics]] */
function PairBreakdown() {
    const { pair } = Route.useParams();
    const { view = "overview", days = DEFAULT_RANGE, filter = "actionable", page = 1 } = Route.useSearch();
    const navigate = Route.useNavigate();
    // The widest range names the token even when the selected one has no window for it.
    const name = useGetOpportunityStats({ days: 90, pair }).data?.top_pairs[0]?.token_name;
    const onView = useCallback(
        (log: LogView, replace?: boolean) => navigate({ search: (prev) => ({ ...prev, ...log }), replace }),
        [navigate],
    );

    return (
        <div className="space-y-3 md:space-y-4">
            <Link
                to="/stats"
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
                <ArrowLeft className="size-4" />
                Overview
            </Link>
            <div className="min-w-0">
                <h2 className="text-xl font-semibold">
                    {name ? <AssetLabel id={tokenIdOfPair(pair)} name={name} /> : "Pair"}
                </h2>
                <p className="truncate font-mono text-xs text-muted-foreground" title={pair}>
                    {pair}
                </p>
            </div>
            <div className="flex gap-1.5" role="tablist" aria-label="Pair view">
                {VIEWS.map((tab) => (
                    <Link
                        key={tab.view}
                        to="."
                        search={(prev) => ({ ...prev, view: tab.view })}
                        role="tab"
                        aria-selected={tab.view === view}
                        className={cn(TAB_CLASS, tab.view === view && TAB_ACTIVE_CLASS)}
                    >
                        {tab.label}
                    </Link>
                ))}
            </div>
            {view === "overview" ? (
                <StatsSummary
                    pair={pair}
                    days={days}
                    onDays={(range) => navigate({ search: (prev) => ({ ...prev, days: range }) })}
                />
            ) : (
                <OpportunityLog pair={pair} view={{ filter, page }} onView={onView} />
            )}
        </div>
    );
}
